import { useEffect, useMemo } from "react";

import {
  AssignmentsCompleteSVG,
  Button,
  Icon,
  QuestionHollowSVG,
  TooltipDialog,
  Modal,
  useModal,
  CloseSVG,
} from "@newsela/angelou";
import useEventRecordPost from "@/usereventrecords/hooks/useEventRecordPost";
import focusElementRefKeys from "@/view/constants/focusElementRefKeys";
import useFocusManager from "@/view/utils/useFocusManager";
import useStudentPreviewContext from "@/view/utils/useStudentPreviewContext";

import useViewportIsNotDesktop from "@/view/utils/useViewportIsNotDesktop";
import { ApiCatalog, useApiQuery } from "@/api";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import {
  headingCss,
  iconCss,
  modalCloseButtonCss,
  modalCss,
  rootCss,
  tooltipDialogCss,
  tooltipDialogFooterButtonCss,
  tooltipDialogIconCss,
} from "./ActivityHeader.style";

interface ActivityHeaderProps {
  isStudent: boolean;
  isArticle: boolean;
  showOnboardingAssignmentBuilder?: boolean;
}

const ActivityHeader = ({
  isStudent,
  isArticle = true,
  showOnboardingAssignmentBuilder,
}: ActivityHeaderProps) => {
  const { setFocusManagerRef } = useFocusManager();
  const { isStudentPreviewOn } = useStudentPreviewContext();
  const { data: features, refetch: refetchFeatures } = useApiQuery({
    apiKey: ApiCatalog.feature.key,
    apiSettings: {
      urlReplacers: [{ id: "me" }],
    },
  });
  const needsAssignmentBuilderOnboarding = useMemo(
    () => features?.includes("needs_assignment_builder_onboarding"),
    [features],
  );
  const [onboardingAnytimeEverywhere] = useGetUserExperimentSegment(
    "onboarding_any_time_everywhere",
  );

  const isEligibleForOnboardingAnytimeEverywhere =
    onboardingAnytimeEverywhere === "on";
  const isMobile = useViewportIsNotDesktop();

  const [postEventRecord] = useEventRecordPost();
  const [modalProps, setActive, triggerRef] = useModal(false);
  useEffect(() => {
    if (isMobile) {
      setActive(false);
    }
  }, [isMobile]);
  return (
    <div css={rootCss}>
      <Icon
        __cssFor={{ root: iconCss }}
        SvgComponent={AssignmentsCompleteSVG}
      />
      <h2
        css={headingCss}
        ref={(ref) => {
          setFocusManagerRef(ref, focusElementRefKeys.activitiesPanelHeader);
        }}
        tabIndex={-1}
      >
        {isStudent || !isArticle || isStudentPreviewOn
          ? "Activities"
          : "Assignment builder"}
      </h2>
      {showOnboardingAssignmentBuilder &&
        isEligibleForOnboardingAnytimeEverywhere &&
        !isStudentPreviewOn &&
        !isMobile && (
          <>
            <TooltipDialog
              buttonContents={
                <Icon
                  SvgComponent={QuestionHollowSVG}
                  __cssFor={tooltipDialogIconCss}
                />
              }
              onClose={async () => {
                if (needsAssignmentBuilderOnboarding) {
                  await postEventRecord({
                    event_name: "assignment_builder_onboarding_dismissed",
                  });
                  await refetchFeatures();
                }
              }}
              buttonProps={{
                flavor: Button.flavors.incognito,
              }}
              __cssFor={tooltipDialogCss}
              dataProps={{
                "data-testid": "assignment_builder_onboarding",
                "data-qa-selector": "assignment-info-button-click",
              }}
              body={
                <div>
                  The assignment builder allows you to select activities for the
                  current article and assign it to your students.
                </div>
              }
              footer={
                <Button
                  onClick={() => setActive(true)}
                  ref={triggerRef}
                  flavor={Button.flavors.incognito}
                  size={Button.sizes.sm}
                  dataProps={{
                    "data-qa-selector": "assignment-info-modal-initiate",
                  }}
                  __cssFor={tooltipDialogFooterButtonCss}
                >
                  See assignment builder demo
                </Button>
              }
              header="How to use the assignment builder"
            />
            <Modal
              {...modalProps}
              onBackgroundClick={() => setActive(false)}
              __cssFor={modalCss}
              dataProps={{
                "data-testid": "assignment_builder_onboarding_modal",
              }}
            >
              <div>
                <Button
                  __cssFor={{ root: modalCloseButtonCss }}
                  flavor={Button.flavors.incognito}
                  ariaProps={{
                    "aria-label": "Close Modal Button",
                  }}
                  dataProps={{
                    "data-qa-selector": "assignment-info-modal-dismiss",
                  }}
                  icon={{ SvgComponent: CloseSVG }}
                  onClick={() => setActive(false)}
                />
                <iframe
                  width="800"
                  height="450"
                  src="https://embed.figma.com/proto/PgSrvwtl3ca0icCWRO3TBd/Onboarding-23-24?page-id=48518%3A20515&node-id=48518-27139&node-type=frame&viewport=388%2C370%2C0.08&scaling=min-zoom&content-scaling=fixed&starting-point-node-id=48518%3A27139&embed-host=share&hide-ui=1"
                  allowFullScreen
                />
              </div>
            </Modal>
          </>
        )}
    </div>
  );
};

export default ActivityHeader;
